/* eslint-disable react/jsx-indent */
import { useUrlGenerator } from '@folklore/routes';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { useUser } from '../../contexts/UserContext';
import Blocks from '../blocks/Blocks';
import StyledButton from '../buttons/StyledButton';
import FilmCard from '../cards/Film';
import PersonCard from '../cards/Person';
import Credits from '../partials/Credits';
import Description from '../partials/Description';
import FilmDetails from '../partials/FilmDetails';
import FilmTrailer from '../partials/FilmTrailer';
import PageMeta from '../partials/PageMeta';
import Projections from '../partials/Projections';
import Slider from '../partials/Slider';
import Tag from '../partials/Tag';
import Title from '../typography/Title';

import styles from '../../../styles/pages/film.module.css';

const propTypes = { page: PropTypes.shape({ id: PropTypes.string }) };

function FilmPage({ page = null }) {
    const route = useUrlGenerator();
    const {
        available = false,
        isPurchased = false,
        mediaUrl = null,
        contents = null,
        blocks = null,
        selectionItems = null,
        events = null,
        images = null,
        title = null,
        credits = null,
        location = null,
        year = null,
        duration = null,
        summary = null,
        free = false,
        id = null,
        type = null,
    } = page || {};

    const user = useUser();

    const selectionCategories = useMemo(
        () =>
            (selectionItems || []).reduce((acc, item) => {
                const { categories: itemCategories = null } = item || {};
                if (itemCategories !== null && itemCategories.length > 0) {
                    return acc.concat(itemCategories.map(({ label = null }) => label));
                }
                return acc;
            }, []),
        [selectionItems],
    );

    const selectionFilms = useMemo(
        () =>
            (selectionItems || []).reduce((acc, item) => {
                const { films: itemFilms = null } = item || {};
                if (itemFilms !== null && itemFilms.length > 0) {
                    return acc.concat(
                        itemFilms
                            .filter((film) => film !== null)
                            .filter(({ id: filmId = null } = {}) => filmId !== id),
                    );
                }
                return acc;
            }, []),
        [selectionItems],
    );

    const directors = useMemo(
        () =>
            (credits || [])
                .filter(
                    ({ roles = [] }) =>
                        (roles || []).map((role) => role.handle).indexOf('director') !== -1,
                )
                .map(({ person = null }) => person?.name)
                .join(', '),
        [credits],
    );

    return (
        <div className={styles.container}>
            <PageMeta page={page} />
            <div className={styles.header}>
                <FilmTrailer className={styles.visual} film={page} />
            </div>
            <div className={styles.top}>
                <div className={styles.left}>
                    <Title className={styles.title} size="large">
                        {title}
                    </Title>
                    <Title className={styles.subtitle} size="small">
                        {directors}
                    </Title>
                    <Title className={styles.infos} size="small">
                        {duration !== null ? (
                            <span className={styles.info}>
                                <FormattedMessage
                                    defaultMessage="{duration} min"
                                    description="Film label"
                                    values={{ duration }}
                                />
                            </span>
                        ) : null}
                        {year !== null ? <span className={styles.info}>— {year}</span> : null}
                        {location !== null ? (
                            <span className={styles.info}>— {location}</span>
                        ) : null}
                    </Title>
                    <div className={styles.tags}>
                        {selectionCategories !== null && selectionCategories.length > 0 ? (
                            <Tag className={styles.tag} size="small">
                                {selectionCategories.join(', ')}
                            </Tag>
                        ) : null}
                        {free ? (
                            <Tag className={styles.tag} size="small">
                                <FormattedMessage
                                    defaultMessage="Gratuit"
                                    description="Film label"
                                />
                            </Tag>
                        ) : null}
                    </div>
                </div>
                <div className={styles.right}>
                    {user !== null && available ? (
                        <StyledButton
                            className={styles.button}
                            href={`${route('pro.orders.create')}?film=${id}`}
                            size="small"
                        >
                            <FormattedMessage
                                defaultMessage="Order now"
                                description="Order now button"
                            />
                        </StyledButton>
                    ) : null}
                    {events !== null && events.length > 0 ? (
                        <StyledButton
                            className={styles.anchor}
                            href="#events"
                            external
                            target="_self"
                            size="small"
                        >
                            <FormattedMessage
                                defaultMessage="See the projections"
                                description="Button label"
                            />
                        </StyledButton>
                    ) : null}
                </div>
            </div>
            <FilmDetails className={styles.details} film={page} />
            {summary !== null ? (
                <Description
                    className={styles.description}
                    titleClassName={styles.titleDescription}
                    title={<FormattedMessage defaultMessage="Synopsis" description="Film label" />}
                    body={summary}
                />
            ) : null}
            {type === 'feature' && selectionItems !== null && selectionItems.length > 0
                ? selectionItems.map((selection) => (
                      <div className={styles.description} key={`selection.${selection?.id}`}>
                          <Description
                              titleClassName={styles.titleDescription}
                              title={
                                  <FormattedMessage
                                      defaultMessage="Programmer's note"
                                      description="Film label"
                                  />
                              }
                              body={selection?.description}
                          />
                          <div className={styles.persons}>
                              {selection?.programmers !== null && selection?.programmers.length > 0
                                  ? selection?.programmers.map((person) => (
                                        <PersonCard
                                            key={`person-${person?.id}`}
                                            className={styles.person}
                                            item={person}
                                        />
                                    ))
                                  : null}
                          </div>
                      </div>
                  ))
                : null}
            {images !== null && images.length > 0 ? (
                <Slider className={styles.images} items={images} cardType="image" size="large" />
            ) : null}
            {credits !== null && credits.length > 0 ? (
                <Credits
                    className={styles.credits}
                    title={<FormattedMessage defaultMessage="Artisans" description="Film label" />}
                    credits={credits}
                />
            ) : null}
            {user !== null && isPurchased ? (
                <div className={styles.content}>
                    <Title className={styles.subtitle}>
                        <FormattedMessage
                            defaultMessage="For the librarians"
                            description="Film label"
                        />
                    </Title>
                    {mediaUrl !== null ? (
                        <StyledButton className={styles.downloadButton} href={mediaUrl} external>
                            <FormattedMessage
                                defaultMessage="Lien de téléchargement du film"
                                description="Film label"
                            />
                        </StyledButton>
                    ) : null}
                    <div className={styles.items}>
                        {contents !== null && contents.length > 0
                            ? (contents || []).map((content) => {
                                  const {
                                      label = null,
                                      url = null,
                                      document = null,
                                  } = content || {};
                                  const { url: documentUrl = null } = document || {};
                                  return (
                                      <StyledButton
                                          key={`content.${label || url}`}
                                          className={styles.contentButton}
                                          size="medium"
                                          href={documentUrl || url}
                                          download
                                          external
                                      >
                                          {label}
                                      </StyledButton>
                                  );
                              })
                            : null}
                    </div>
                </div>
            ) : null}

            {events !== null && events.length > 0 ? (
                <Projections
                    id="events"
                    className={styles.events}
                    title={
                        <FormattedMessage
                            defaultMessage="Upcoming projections"
                            description="Film label"
                        />
                    }
                    size="small"
                    events={events}
                />
            ) : null}

            {selectionFilms !== null && selectionFilms.length > 0 ? (
                <div className={styles.related}>
                    <Title className={styles.subtitle} size="small">
                        <FormattedMessage defaultMessage="Related films" description="Film label" />
                    </Title>
                    {(selectionFilms || []).map((film) => (
                        <FilmCard
                            key={`selectionfilm.${film.id}`}
                            className={styles.film}
                            item={film}
                        />
                    ))}
                </div>
            ) : null}

            {blocks !== null && blocks.length > 0 ? (
                <Blocks className={styles.blocks} blocks={blocks} padded={false} withoutLines />
            ) : null}
        </div>
    );
}

FilmPage.propTypes = propTypes;

export default FilmPage;
